import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification, setConnected } from '../store/slices/notificationsSlice';
import { updateLeadInList } from '../store/slices/leadsSlice';

const useWebSocket = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Determine WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = process.env.REACT_APP_WS_URL || `${wsProtocol}//${window.location.host}`;
    const wsUrl = `${wsHost}/ws/notifications/?token=${encodeURIComponent(token)}`;

    // Connect to Django Channels WebSocket
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      dispatch(setConnected(true));
      console.log('WebSocket connected');
    };

    socketRef.current.onclose = (event) => {
      dispatch(setConnected(false));
      console.log('WebSocket disconnected:', event.code, event.reason);
      
      // Attempt to reconnect after 3 seconds
      setTimeout(() => {
        if (!socketRef.current || socketRef.current.readyState === WebSocket.CLOSED) {
          // Reconnect
          const newSocket = new WebSocket(wsUrl);
          socketRef.current = newSocket;
          
          newSocket.onopen = socketRef.current.onopen;
          newSocket.onclose = socketRef.current.onclose;
          newSocket.onmessage = socketRef.current.onmessage;
          newSocket.onerror = socketRef.current.onerror;
        }
      }, 3000);
    };

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        if (data.type === 'notification') {
          dispatch(addNotification({
            id: data.id || Date.now(),
            title: data.title,
            message: data.message,
            type: data.notification_type || 'info',
            data: data.data,
            is_read: false,
            created_at: data.created_at || new Date().toISOString(),
          }));

          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(data.title, {
              body: data.message,
              icon: '/favicon.ico',
            });
          }
        } else if (data.type === 'lead_update') {
          dispatch(updateLeadInList(data.lead));
        }
      } catch (error) {
        console.error('WebSocket message parsing error:', error);
      }
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      dispatch(setConnected(false));
    };

    return () => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
    };
  }, [dispatch]);

  // Request notification permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return socketRef.current;
};

export default useWebSocket;